/**
 * Typed client for OUR backend API (SPA -> backend). Mirrors spec §4.
 *
 * All requests are same-origin (or proxied in dev), credentials:'include' so the
 * httpOnly session cookie rides along. The upstream m10n cookie never reaches here.
 */

// --- Filter model (matches backend app.mob.serialize) ---

export type FilterOperator =
  | "eq"
  | "ieq"
  | "contains"
  | "includes"
  | "starts"
  | "ends"
  | "exists"
  | "is_empty"
  | "among"
  | "bday"
  | "gte"
  | "lte"
  | "from"
  | "to"
  | "between"
  | "between_numeric"
  | "child_of"
  | "in";

export interface FilterClause {
  field: string;
  operator: FilterOperator;
  value: unknown;
}

/** Canonical saved-filter envelope (logic is always "and" upstream). */
export interface FilterEnvelope {
  logic: "and";
  filters: FilterClause[];
}

// --- Response types ---

export interface SessionInfo {
  user: string;
  region?: string | null;
  /** ER login (email). Present on whoami; may be absent on older/legacy responses. */
  er_login?: string | null;
  /** True when this session belongs to a super-admin. Source of truth: backend DB. */
  is_admin?: boolean;
}

export interface VolunteersPage {
  count: number;
  results: Volunteer[];
}

/** A volunteer row. 159 fields upstream; loosely typed plus known MVP columns. */
export interface Volunteer {
  id: number;
  last_name: string;
  first_name: string;
  patronymic?: string | null;
  birthday?: string | null;
  phone_mobile?: string | null;
  branch_id?: number | null;
  branch_name?: string | null;
  region_name?: string | null;
  loyalty?: string | null;
  readiness?: string | null;
  [key: string]: unknown;
}

export interface CountResponse {
  count: number;
}

/** mob /api/refs aggregator (dropdown source). Loosely typed; keys per MOB_API_REVERSE.md. */
export interface Refs {
  targetGroups: Array<{ id: number; title?: string; name?: string }>;
  citizenCategories: Array<{ id: number; title?: string; name?: string }>;
  status: string[];
  /** Upstream sends a {code: label} dict (e.g. {"-":"Беспартийный","m":"Член партии"}),
   *  NOT an array — resolveOptions() handles both shapes (Xfe-7). */
  membership: Record<string, string>;
  loyalty: string[];
  branches: Array<{ id: number; title?: string; name?: string }>;
  uiks: unknown[];
  extraFields: unknown[];
  operators: unknown;
  touchTypes: unknown[];
  touchResultTypes: unknown[];
  bulkEditMaxTotal: number;
  canSeeExcelExport: boolean;
  minBirthdayDateIso?: string;
  [key: string]: unknown;
}

export interface SavedFilter {
  id: number;
  name: string;
  filter: FilterEnvelope;
}

export interface JobInfo {
  id: number;
  kind: string;
  status: "pending" | "running" | "done" | "failed" | "cancelled" | "scheduled";
  total: number;
  progress: number;
  /** ISO-8601 UTC string; set for scheduled (and once-scheduled) jobs. */
  run_at?: string | null;
  error?: string | null;
  result_ref?: string | null;
  failed: unknown[];
  /** Human-readable summary (e.g. «Касание №385785 · результат «Да»»); null = fall back to kind label. */
  description?: string | null;
  /** For touch_fill jobs: the target touch id (link target); null/absent for other kinds. */
  touch_id?: number | null;
  /** For touch_fill jobs: the touch-result value applied (e.g. «Да»); null when n/a. */
  result?: string | null;
  /**
   * Сколько людей планируется обработать: ids→len(ids); sample_n→min(sample_n,count);
   * filter→count из selection; null когда неизвестно.
   */
  planned_total?: number | null;
}

/** Lightweight row for the "Запланированные задачи" widget (GET /api/jobs). */
export interface JobListItem {
  id: number;
  kind: string;
  status: JobInfo["status"];
  total: number;
  progress: number;
  run_at: string | null;
  created_at: string;
  error?: string | null;
  failed_count: number;
  /** Human-readable summary (e.g. «Касание №385785 · результат «Да»»); null = fall back to kind label. */
  description?: string | null;
  /** For touch_fill jobs: the target touch id (link target); null/absent for other kinds. */
  touch_id?: number | null;
  /** For touch_fill jobs: the touch-result value applied (e.g. «Да»); null when n/a. */
  result?: string | null;
  /**
   * Сколько людей планируется обработать: ids→len(ids); sample_n→min(sample_n,count);
   * filter→count из selection; null когда неизвестно.
   */
  planned_total?: number | null;
}

export interface ListParams {
  filters: FilterClause[];
  page?: number;
  pageSize?: number;
  sort?: string | null;
}

// --- Touches (касания) ---

/** A "cell" (ячейка) node in the cell tree. Loosely typed plus known fields. */
export interface Cell {
  id: number;
  title: string;
  parent_id?: number | null;
  /** Present only if mob exposes it; otherwise children are discovered lazily. */
  has_children?: boolean;
  [key: string]: unknown;
}

/** Size/access info for a cell (GET /api/cells-size/{id}). */
export interface CellsSize {
  size: number;
  origin_volunteers_count: number;
  fullAccess?: boolean;
  [key: string]: unknown;
}

/** A primary organization (первичное отделение) under a branch. */
export interface PrimaryOrg {
  id: number;
  title?: string;
  name?: string;
  [key: string]: unknown;
}

/** A polling station (УИК) under a branch. */
export interface Uik {
  id: number;
  title?: string;
  name?: string;
  number?: number | string;
  [key: string]: unknown;
}

/** A cell-type / touch-result-type reference row. Loosely typed plus known fields. */
export interface CellType {
  id: number;
  title: string;
  parent_id?: number | null;
  [key: string]: unknown;
}

/** A touch-result-type reference (e.g. Да/Нет). Loosely typed plus known fields. */
export interface TouchResultType {
  id: number;
  title?: string;
  name?: string;
  [key: string]: unknown;
}

/** A touch (касание). 197k backend; loosely typed plus known MVP fields. */
export interface Touch {
  id: number;
  name: string;
  cell_id?: number | null;
  cell_title?: string | null;
  results?: number | null;
  is_active?: boolean | null;
  date_start?: string | null;
  date_end?: string | null;
  touch_result_type_id?: number | null;
  [key: string]: unknown;
}

export interface TouchesPage {
  count: number;
  results: Touch[];
}

/** Composition kinds for GET /api/touches/{id}/results. */
export type TouchCompositionKind = "volunteers" | "general" | "cells";

/** A single composition row (volunteer / general / cell). Loosely typed. */
export interface TouchResultRow {
  /** The result-record id (used by DELETE /api/touches/results/{result_id}). */
  id: number;
  [key: string]: unknown;
}

export interface TouchCompositionPage {
  count: number;
  results: TouchResultRow[];
}

export interface TouchCompositionParams {
  page?: number;
  pageSize?: number;
  sort?: string | null;
}

/** Form payload for POST /api/touches. Loosely typed beyond the required fields. */
export interface CreateTouchForm {
  name: string;
  cell_id: number;
  touch_type_id: number;
  touch_result_type_id: number;
  date_start: string;
  date_end?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

/** Selection payload reused by fill (mirrors the Selection store shape). */
export interface TouchSelection {
  mode: SelectionMode;
  ids?: number[];
  filters?: FilterClause[];
  /**
   * Информационный размер выборки на момент отправки (из стора selection);
   * задаётся только при mode='filter'; бэкенд сохраняет его в params и считает
   * planned_total.
   */
  count?: number | null;
}

export type SelectionMode = "ids" | "filter";

/** Selection payload for bulk operations (same shape as touches). */
export type BulkSelection = TouchSelection;

/**
 * Values map for a bulk field update — {field: value}. On the wire, booleans are
 * sent as the string 'y'/'n' (the UI already emits 'y'/'n' for the is_ flags and
 * "verified"); the *_ids multiselects are number[] (mob expects [int]).
 */
export interface BulkUpdateValues {
  [field: string]: string | number | boolean | string[] | number[];
}

/** Body for POST /api/touches/{id}/fill. */
export interface FillTouchBody {
  cell_id: number;
  result: string;
  description?: string | null;
  selection: TouchSelection;
  /** ISO-8601 UTC string; null/omitted = run now, otherwise schedule the job. */
  run_at?: string | null;
  /**
   * Server-side random sample size: null/omitted = whole selection. Only honored
   * when selection.mode==='filter' (the job samples N random ids from the full
   * filter result); ignored for selection.mode==='ids' (use selectRandomN there).
   */
  sample_n?: number | null;
}

// --- Low-level fetch helper ---

/** Backend API base. Same-origin in prod; Vite dev-server proxies /api -> backend. */
const API_BASE = "";

/** Thrown on a non-2xx response. Carries the HTTP status so callers can branch (e.g. 401). */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function buildError(res: Response): Promise<ApiError> {
  let body: unknown = undefined;
  let message = `${res.status} ${res.statusText}`;
  try {
    const text = await res.text();
    if (text) {
      try {
        body = JSON.parse(text);
        const detail = (body as { detail?: unknown } | null)?.detail;
        if (typeof detail === "string") message = detail;
        else if (Array.isArray(detail) && detail.length > 0) {
          message = detail
            .map((d) => (typeof d === "object" && d && "msg" in d ? String((d as { msg: unknown }).msg) : String(d)))
            .join("; ");
        }
      } catch {
        body = text;
        message = text;
      }
    }
  } catch {
    /* ignore body read failures */
  }
  return new ApiError(res.status, message, body);
}

/** Internal fetch wrapper: prefixes base, sends credentials, parses JSON, throws on !ok. */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers,
  });

  if (!res.ok) throw await buildError(res);

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// --- Session ---

export async function login(erLogin: string, erPassword: string): Promise<SessionInfo> {
  return apiFetch<SessionInfo>("/api/session", {
    method: "POST",
    body: JSON.stringify({ er_login: erLogin, er_password: erPassword }),
  });
}

export async function whoami(): Promise<SessionInfo> {
  return apiFetch<SessionInfo>("/api/session", { method: "GET" });
}

export async function logout(): Promise<void> {
  await apiFetch<void>("/api/session", { method: "DELETE" });
}

// --- Reference data ---

export async function getRefs(): Promise<Refs> {
  return apiFetch<Refs>("/api/refs", { method: "GET" });
}

// --- Volunteers ---

export async function listVolunteers(params: ListParams): Promise<VolunteersPage> {
  const qs = new URLSearchParams();
  qs.set("filters", JSON.stringify(params.filters ?? []));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));
  if (params.sort) qs.set("sort", params.sort);
  return apiFetch<VolunteersPage>(`/api/volunteers?${qs.toString()}`, { method: "GET" });
}

export async function countVolunteers(filters: FilterClause[]): Promise<CountResponse> {
  const qs = new URLSearchParams();
  qs.set("filters", JSON.stringify(filters ?? []));
  return apiFetch<CountResponse>(`/api/volunteers/count?${qs.toString()}`, { method: "GET" });
}

/** Response of POST /api/volunteers/sample. Mirrors backend SampleResponse byte-for-byte. */
export interface SampleResponse {
  /** <= n random volunteer ids (server-side Fisher-Yates over fetched pages). */
  ids: number[];
  /** count() for the filter (UI shows "из total"). */
  total: number;
  /** Echo of n after clamping to total. */
  requested: number;
}

/**
 * Pick up to `n` random volunteers matching `filters` (POST /api/volunteers/sample).
 * Body shape `{ filters, n }` and response `{ ids, total, requested }` must match the
 * backend SampleRequest/SampleResponse Pydantic models exactly.
 */
export async function sampleVolunteers(
  filters: FilterClause[],
  n: number,
): Promise<SampleResponse> {
  return apiFetch<SampleResponse>("/api/volunteers/sample", {
    method: "POST",
    body: JSON.stringify({ filters: filters ?? [], n }),
  });
}

/** Triggers the xlsx export download (POST /api/volunteers/export). Resolves when started. */
export async function exportVolunteers(
  filters: FilterClause[],
  sort?: string | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/volunteers/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "*/*" },
    body: JSON.stringify({ filters: filters ?? [], sort: sort ?? null }),
  });
  if (!res.ok) throw await buildError(res);

  const blob = await res.blob();
  const filename = parseFilename(res.headers.get("Content-Disposition")) ?? "volunteers.xlsx";
  triggerDownload(blob, filename);
}

/** Extracts the filename from a Content-Disposition header (RFC 5987 filename* or plain). */
function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() ?? null;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --- Bulk operations over the current selection (Wave 4a) ---

/**
 * Set one value for every volunteer in `selection` (POST /api/volunteers/bulk-update).
 * One server request — the backend applies it via upstream PUT by ids/filters.
 * Body `{selection, values}` and response `{count}` mirror the backend
 * BulkUpdateRequest / BulkUpdateResponse byte-for-byte.
 */
export async function bulkUpdateVolunteers(
  selection: BulkSelection,
  values: BulkUpdateValues,
): Promise<{ count: number }> {
  return apiFetch<{ count: number }>("/api/volunteers/bulk-update", {
    method: "POST",
    body: JSON.stringify({ selection, values }),
  });
}

/**
 * Delete every volunteer in `selection` (POST /api/volunteers/bulk-delete). mob has
 * no bulk-delete, so the backend runs a resumable DELETE-loop job; the returned
 * job_id drives <JobProgress/>. Body `{selection}` / response `{job_id}` mirror the
 * backend BulkDeleteRequest / BulkDeleteResponse byte-for-byte.
 */
export async function bulkDeleteVolunteers(
  selection: BulkSelection,
): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>("/api/volunteers/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ selection }),
  });
}

// --- Readiness (массовая «Готовность регион» по списку id из Excel, Wave 4c) ---

/**
 * Bulk-set readiness_region=value for the given volunteer ids
 * (POST /api/volunteers/readiness/apply). There is no upstream batch endpoint for
 * this, so the backend spawns a resumable job that bulk-updates readiness_region in
 * chunks (<= refs.bulkEditMaxTotal per batch); the returned job_id drives the
 * «Задачи» list + header indicator. `ids` must be unique volunteer_id numbers (>=1)
 * and `value` one of «Да» | «Нет» | «На проверке». Body `{ids, value}` / response
 * `{job_id}` mirror the backend byte-for-byte.
 */
export async function applyReadiness(body: {
  ids: number[];
  value: string;
}): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>("/api/volunteers/readiness/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Обновление из файла (один признак всем по ключу id/телефон, Wave 4d) ---

/**
 * Bulk-set ONE editable field to ONE value for every row keyed by `keys`
 * (POST /api/volunteers/bulk-edit-from-file/apply). `key_type` chooses how the keys map
 * to volunteers: 'id' (numeric volunteer_id — the «ID в БД» column) or 'phone' (phone
 * strings AS TYPED from the file — the backend job normalizes each to the last 10 digits
 * and resolves it upstream by count-first). `values` is ONE field -> ONE value (same
 * shape as the «Изменить признаки» dialog; build it with FieldValuePicker/buildValue).
 * There is no upstream batch endpoint, so the backend spawns a resumable job; the returned
 * job_id drives «Задачи» + the header indicator. Body `{key_type, keys, values}` / response
 * `{job_id}` mirror the backend BulkEditFromFileRequest / BulkEditFromFileResponse.
 */
export async function applyBulkEditFile(body: {
  key_type: "id" | "phone";
  keys: (number | string)[];
  values: Record<string, unknown>;
}): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>("/api/volunteers/bulk-edit-from-file/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- УИК → отделение из файла (проставить uik_id + branch_id по номеру УИК, Wave 4e) ---

/** One parsed file row for the УИК-import: a key (id/phone) + the УИК number from the file. */
export interface UikBranchFileRow {
  /** Key AS TYPED: numeric volunteer_id (as string) for key_type='id', raw phone for 'phone'. */
  key: string;
  /** УИК number from the file (the `n` field of refs.uiks). String or number as read. */
  uik_n: number | string;
}

/**
 * For every row keyed by `key`, set the volunteer's УИК (uik_id) AND local branch (branch_id)
 * derived on the SERVER from the УИК number (POST /api/volunteers/uik-branch-from-file/apply).
 * The client sends ONLY `{key, uik_n}` per row — it never computes the branch. `key_type` chooses
 * how keys map to volunteers: 'id' (numeric volunteer_id, «ID в БД») or 'phone' (phone strings AS
 * TYPED; the backend normalizes each to the last 10 digits and resolves count-first). The backend
 * maps uik_n → {uik_id, branch_id} via refs.uiks, runs a safety-probe on ONE volunteer before the
 * mass write, then spawns a resumable job (kind `uik_branch_fill`); the returned job_id drives
 * «Задачи» + the header indicator. Body `{key_type, rows}` / response `{job_id}` mirror the backend.
 */
export async function applyUikBranchFile(body: {
  key_type: "id" | "phone";
  rows: UikBranchFileRow[];
}): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>("/api/volunteers/uik-branch-from-file/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Data Updater (массовое обновление по файлу Excel, Wave 4b) ---

/**
 * A column descriptor from the preview's `validColumns`. Pass-through: we keep
 * id/name/isIdentifier and any other keys mob emits and send them back verbatim
 * as the chosen `fields`. NO whitelist — index signature preserves unknown keys.
 */
export interface DataUpdaterColumn {
  id: string | number;
  name: string;
  isIdentifier?: boolean;
  [k: string]: unknown;
}

/** A per-row validation error from the preview's `stats.errors`. */
export interface DataUpdaterError {
  line: number;
  error_array: unknown[];
  [k: string]: unknown;
}

/** Aggregate preview stats. `successes` = rows with no errors (the apply count). */
export interface DataUpdaterStats {
  count: number;
  successes: number;
  errors: DataUpdaterError[];
  mistakes?: unknown;
  [k: string]: unknown;
}

/** Phase-1 preview body (verbatim from mob). */
export interface DataUpdaterPreview {
  stats: DataUpdaterStats;
  validColumns: DataUpdaterColumn[];
  items: Array<Record<string, unknown>>;
  [k: string]: unknown;
}

/** Match method for phase 2. */
export type DataUpdaterMethod = "by_phone" | "by_fio_birthday";

/** Phase-2 apply request. fields/updatedRows/stats are pass-through from preview. */
export interface DataUpdaterApplyRequest {
  fields: DataUpdaterColumn[];
  updatedRows: Array<Record<string, unknown>>;
  stats: DataUpdaterStats;
  method: DataUpdaterMethod;
}

/** Phase-2 apply result (pass-through; surface results/errors/failed if present). */
export interface DataUpdaterApplyResult {
  [k: string]: unknown;
}

/**
 * Phase 1 — preview the xlsx WITHOUT writing (POST /api/volunteers/data-updater/preview).
 * Uses FormData + raw fetch: the browser sets multipart/form-data + boundary, so we must
 * NOT go through apiFetch (it forces a JSON Content-Type) and must NOT set Content-Type here.
 */
export async function dataUpdaterPreview(file: File): Promise<DataUpdaterPreview> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/api/volunteers/data-updater/preview`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" }, // intentionally NO Content-Type
    body: fd,
  });
  if (!res.ok) throw await buildError(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as DataUpdaterPreview;
}

/**
 * Phase 2 — apply to the live base (POST /api/volunteers/data-updater/apply). JSON body
 * sent verbatim: the chosen validColumns objects as `fields`, the preview `items` as
 * `updatedRows`, preview `stats`, and the user-chosen `method`.
 */
export async function dataUpdaterApply(
  body: DataUpdaterApplyRequest,
): Promise<DataUpdaterApplyResult> {
  return apiFetch<DataUpdaterApplyResult>("/api/volunteers/data-updater/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Download the Data Updater xlsx template (GET /api/volunteers/data-updater/template). */
export async function downloadDataUpdaterTemplate(): Promise<void> {
  const res = await fetch(`${API_BASE}/api/volunteers/data-updater/template`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "*/*" },
  });
  if (!res.ok) throw await buildError(res);
  const blob = await res.blob();
  const filename =
    parseFilename(res.headers.get("Content-Disposition")) ?? "data_updater_template.xlsx";
  triggerDownload(blob, filename);
}

// --- Saved filters ---

export async function getSavedFilters(): Promise<SavedFilter[]> {
  return apiFetch<SavedFilter[]>("/api/filters", { method: "GET" });
}

export async function saveFilter(name: string, filter: FilterEnvelope): Promise<{ id: number }> {
  return apiFetch<{ id: number }>("/api/filters", {
    method: "POST",
    body: JSON.stringify({ name, filter }),
  });
}

export async function deleteFilter(id: number): Promise<void> {
  await apiFetch<void>(`/api/filters/${id}`, { method: "DELETE" });
}

// --- Jobs ---

export async function getJob(id: number): Promise<JobInfo> {
  return apiFetch<JobInfo>(`/api/jobs/${id}`, { method: "GET" });
}

/** List the current user's jobs (GET /api/jobs), optionally filtered by status/kind. */
export async function listJobs(
  params: { status?: JobInfo["status"]; kind?: string } = {},
): Promise<JobListItem[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.kind) qs.set("kind", params.kind);
  const query = qs.toString();
  return apiFetch<JobListItem[]>(`/api/jobs${query ? `?${query}` : ""}`, { method: "GET" });
}

/** Cancel a job (POST /api/jobs/{id}/cancel). Works for SCHEDULED and RUNNING. 204. */
export async function cancelJob(id: number): Promise<void> {
  await apiFetch<void>(`/api/jobs/${id}/cancel`, { method: "POST" });
}

// --- Touches (касания) ---

export interface ListTouchesParams {
  filters?: FilterClause[];
  page?: number;
  pageSize?: number;
  sort?: string | null;
}

export async function listTouches(params: ListTouchesParams = {}): Promise<TouchesPage> {
  const qs = new URLSearchParams();
  qs.set("filters", JSON.stringify(params.filters ?? []));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));
  qs.set("sort", params.sort ?? "-id");
  return apiFetch<TouchesPage>(`/api/touches?${qs.toString()}`, { method: "GET" });
}

export async function getTouchComposition(
  touchId: number,
  kind: TouchCompositionKind,
  params: TouchCompositionParams = {},
): Promise<TouchCompositionPage> {
  const qs = new URLSearchParams();
  qs.set("kind", kind);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));
  if (params.sort) qs.set("sort", params.sort);
  return apiFetch<TouchCompositionPage>(
    `/api/touches/${touchId}/results?${qs.toString()}`,
    { method: "GET" },
  );
}

export async function createTouch(form: CreateTouchForm): Promise<Touch> {
  return apiFetch<Touch>("/api/touches", {
    method: "POST",
    body: JSON.stringify(form),
  });
}

export async function deleteTouch(id: number): Promise<void> {
  await apiFetch<void>(`/api/touches/${id}`, { method: "DELETE" });
}

/** Remove a single activist from a touch (by composition result-record id). */
export async function removeFromTouch(resultId: number): Promise<void> {
  await apiFetch<void>(`/api/touches/results/${resultId}`, { method: "DELETE" });
}

export async function fillTouch(
  touchId: number,
  body: FillTouchBody,
): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>(`/api/touches/${touchId}/fill`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Cells (cell tree, lazy) ---

export interface GetCellsParams {
  parentId?: number;
  title?: string;
}

/**
 * List cells. With no params returns the roots (mob: parent_id<100 -> null).
 * With `parentId` returns that node's direct children; with `title` returns a
 * flat list of name matches (used by the search box in <CellTreeSelect>).
 */
export async function getCells(params: GetCellsParams = {}): Promise<Cell[]> {
  const qs = new URLSearchParams();
  if (params.parentId != null) qs.set("parent_id", String(params.parentId));
  if (params.title != null && params.title.trim() !== "") qs.set("title", params.title);
  const query = qs.toString();
  return apiFetch<Cell[]>(`/api/cells${query ? `?${query}` : ""}`, { method: "GET" });
}

/**
 * Search cells by title substring against OUR backend (GET /api/cells/search).
 * The backend serves a FLAT index of the whole cell tree (traversal + cache), so
 * this is reliable — unlike mob's ?title= (broken on Cyrillic). Returns Cell objects
 * (same shape as getCells), title-sorted, capped at `limit`. Blank q short-circuits.
 */
export interface CellSearchResult {
  /** `building` = flat index still being built in the background; poll until `ready`. */
  status: "ready" | "building";
  results: Cell[];
}

export async function searchCells(q: string, limit = 50): Promise<CellSearchResult> {
  if (q.trim() === "") return { status: "ready", results: [] };
  const qs = new URLSearchParams();
  qs.set("q", q);
  qs.set("limit", String(limit));
  return apiFetch<CellSearchResult>(`/api/cells/search?${qs.toString()}`, { method: "GET" });
}

/** Size/access info for a single cell. */
export async function getCellsSize(cellId: number): Promise<CellsSize> {
  return apiFetch<CellsSize>(`/api/cells-size/${cellId}`, { method: "GET" });
}

// --- Cascade (branch -> primary / uik) ---

/** Primary organizations under a branch (GET /api/branches/{branchId}/primaries). */
export async function getPrimaries(branchId: number): Promise<PrimaryOrg[]> {
  return apiFetch<PrimaryOrg[]>(`/api/branches/${branchId}/primaries`, { method: "GET" });
}

/** Polling stations (УИК) under a branch (GET /api/branches/{branchId}/uiks). */
export async function getUiks(branchId: number): Promise<Uik[]> {
  return apiFetch<Uik[]>(`/api/branches/${branchId}/uiks`, { method: "GET" });
}

export async function getCellTypes(): Promise<CellType[]> {
  return apiFetch<CellType[]>("/api/cell-types", { method: "GET" });
}

// --- Activist card (карточка активиста) ---

/** One row of a volunteer's touch history (GET /api/volunteers/{id}/history). Loosely typed. */
export interface VolunteerHistoryRow {
  id?: number;
  touch_id?: number;
  touch_name?: string;
  name?: string;
  result?: string;
  date?: string;
  [k: string]: unknown;
}

export interface VolunteerHistoryPage {
  count: number;
  results: VolunteerHistoryRow[];
}

/**
 * Fetch a single volunteer for the activist card. mob has NO by-id endpoint
 * (MOB_API_REVERSE.md p.55) — we read it out of the listing via an id `among`
 * filter, exactly like TouchDetail pulls one touch.
 */
export async function getVolunteer(id: number): Promise<Volunteer | undefined> {
  const page = await listVolunteers({
    filters: [{ field: "id", operator: "among", value: [id] }],
    page: 1,
    pageSize: 1,
  });
  return page.results.find((v) => v.id === id) ?? page.results[0];
}

/** A volunteer's touch history — a real upstream sub-resource (GET /api/volunteers/{id}/history). */
export async function getVolunteerHistory(id: number): Promise<VolunteerHistoryPage> {
  return apiFetch<VolunteerHistoryPage>(`/api/volunteers/${id}/history`, { method: "GET" });
}

// --- Admin (суперадмин, только просмотр) ---

/** One row of GET /api/admin/users — aggregate per ER account across all sessions. */
export interface AdminUser {
  er_login: string;
  user: string;
  region?: string | null;
  sessions_total: number;
  sessions_active: number;
  last_seen_at?: string | null;
  jobs_total: number;
  jobs_running: number;
  is_admin: boolean;
}

/** One row of GET /api/admin/jobs — a job across ALL users (no owns-filter). */
export interface AdminJob {
  id: number;
  kind: string;
  status: JobInfo["status"];
  er_login: string;
  description?: string | null;
  total: number;
  progress: number;
  created_at: string;
  run_at?: string | null;
}

/** One row of GET /api/admin/audit — action log, newest first. */
export interface AdminAudit {
  id: number;
  created_at: string;
  er_login?: string | null;
  action: string;
  target?: string | null;
  route_name?: string | null;
}

/** One egress route in GET /api/admin/system. */
export interface AdminRoute {
  name: string;
  healthy: boolean;
  in_flight: number;
  quarantined_seconds: number;
  egress_country?: string | null;
}

/** GET /api/admin/system — egress/throttle state + global job counters. */
export interface AdminSystem {
  routes: AdminRoute[];
  /** Global cap on simultaneous requests to mob across the WHOLE server. */
  concurrency_max: number;
  jobs_running: number;
  jobs_scheduled: number;
  jobs_pending: number;
  rate_per_sec: number;
  breaker_threshold: number;
}

export interface AdminJobsParams {
  er_login?: string;
  status?: JobInfo["status"];
  kind?: string;
  limit?: number;
}

export interface AdminAuditParams {
  er_login?: string;
  action?: string;
  limit?: number;
}

/** Aggregate per-user overview across all ER accounts (GET /api/admin/users). */
export async function getAdminUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>("/api/admin/users", { method: "GET" });
}

/** Every user's jobs (no owns-filter), optionally filtered (GET /api/admin/jobs). */
export async function getAdminJobs(params: AdminJobsParams = {}): Promise<AdminJob[]> {
  const qs = new URLSearchParams();
  if (params.er_login) qs.set("er_login", params.er_login);
  if (params.status) qs.set("status", params.status);
  if (params.kind) qs.set("kind", params.kind);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return apiFetch<AdminJob[]>(`/api/admin/jobs${query ? `?${query}` : ""}`, { method: "GET" });
}

/** Action audit log, newest first, optionally filtered (GET /api/admin/audit). */
export async function getAdminAudit(params: AdminAuditParams = {}): Promise<AdminAudit[]> {
  const qs = new URLSearchParams();
  if (params.er_login) qs.set("er_login", params.er_login);
  if (params.action) qs.set("action", params.action);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return apiFetch<AdminAudit[]>(`/api/admin/audit${query ? `?${query}` : ""}`, { method: "GET" });
}

/** Egress/throttle state + global job counters (GET /api/admin/system). */
export async function getAdminSystem(): Promise<AdminSystem> {
  return apiFetch<AdminSystem>("/api/admin/system", { method: "GET" });
}

/* ───────────── ЕДГ-лотерея (mob-api.er.ru) ───────────── */

/** GET /api/lottery/status — настроена ли учётка + счётчики журнала отправленных. */
export interface LotteryStatus {
  configured: boolean;
  region: number;
  login: string | null;
  sent_total: number;
  last_sent_at: string | null;
}

/** POST /api/lottery/turnout — выборка корзины; сервер сам добавит readiness_region=Да. */
export interface LotteryTurnoutBody {
  selection: TouchSelection;
  /** true — слать и тех, кто уже есть в журнале отправленных. */
  resend_all: boolean;
  /** случайно N из выборки, только mode='filter' */
  sample_n?: number | null;
}

export interface LotterySettingsInfo {
  configured: boolean;
  region: number;
  login: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface LotterySettingsBody {
  region: number;
  login: string;
  password: string;
}

export interface LotteryTestResult {
  ok: boolean;
  message: string;
  status: number | null;
}

export async function getLotteryStatus(): Promise<LotteryStatus> {
  return apiFetch<LotteryStatus>("/api/lottery/status", { method: "GET" });
}

export async function submitLotteryTurnout(body: LotteryTurnoutBody): Promise<{ job_id: number }> {
  return apiFetch<{ job_id: number }>("/api/lottery/turnout", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getAdminLotterySettings(): Promise<LotterySettingsInfo> {
  return apiFetch<LotterySettingsInfo>("/api/admin/lottery/settings", { method: "GET" });
}

export async function putAdminLotterySettings(body: LotterySettingsBody): Promise<LotterySettingsInfo> {
  return apiFetch<LotterySettingsInfo>("/api/admin/lottery/settings", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function testAdminLotterySettings(): Promise<LotteryTestResult> {
  return apiFetch<LotteryTestResult>("/api/admin/lottery/test", { method: "POST" });
}
